import React, { Fragment, useState } from 'react';
import { calendlyLinks, csColors } from '../../constants';
import { track } from '../../tracking';
import { captureError } from '../../pages/captureError';
import { DocProfileResponse } from '../../api/fetchDoctorProfile';
import { EkaLogoCSCard } from './Icons';
import { useHistory } from 'react-router-dom';
import { GO_TO_BOOK_TRAINING, GO_TO_SUBSCRIBE } from '../../routes/paths';
import sendToSlack from '../../api/sendToSlack';
import { Modal, notification } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { openCrispChat } from '../../pages/utils';

const CSCard: React.FC<{
    planIcon: React.ReactNode;
    plan: 'PLUS' | 'PRO' | 'TRAIL';
    doc: DocProfileResponse | undefined;
    hasSm: boolean;
}> = (props) => {
    const { planIcon, plan, doc, hasSm } = props;
    const history = useHistory();

    const [isNoteModalVisible, setNoteModalVisible] = useState(false);
    const [callNotes, setCallNotes] = useState('');

    const planType = plan as keyof typeof csColors;

    const isTrail = plan === 'TRAIL';

    const heading = isTrail
        ? 'Get your Dedicated Success Manager'
        : 'Your dedicated Success Manager';

    const subHeading = isTrail ? (
        'With Eka Pro, you get a success manager, to assist you at every step of you digitisation journey with eka care.'
    ) : (
        <Fragment>
            <span className="capitalize">
                {doc?.profile.professional.cs?.success_manager ?? ''}
            </span>{' '}
            is dedicated to empowering your growth on Eka.care. Connect for any queries or to
            optimize your experience.
        </Fragment>
    );

    const goToSubscribe = () => history.push(GO_TO_SUBSCRIBE);

    const canScheduleCall =
        doc?.profile.professional.cs?.oid &&
        Object.keys(calendlyLinks).includes(doc?.profile.professional?.cs?.oid);

    const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const note = e.target.value;
        setCallNotes(note);
    };

    const gotoRequestTraining = () => {
        track('GO_TO_BOOK_TRAINING_NEW_CTA');
        history.push(GO_TO_BOOK_TRAINING);
    };

    const requestCallback = () => {
        pushRequestCallbackToSlack({
            'Doctor Name': doc?.profile.personal.name.f || '',
            'Doctor ID': doc?._id || '',
            Time: new Date().toLocaleString(),
            'Success Manager': doc?.profile.professional.cs?.success_manager ?? '',
            Role: doc?.loginmeta?.role || '',
            'Call Notes': callNotes,
        });
        setNoteModalVisible(false);
        setCallNotes('');
        notification.success({
            message: 'Callback Requested',
            description: 'Your request has been sent to your Success Manager.',
            duration: 5,
        });
    };

    const secondaryStyle = `${!isTrail ? csColors[planType].cta['chat-now'].text : ''} ${
        !isTrail ? csColors[planType].cta['chat-now'].bg : ''
    } ${
        !isTrail ? csColors[planType].cta['chat-now'].border : ''
    } flex-grow border flex py-6 px-8 font-700 rounded-4 items-center justify-center cursor-pointer`;

    const primaryStyle = `${!isTrail ? csColors[planType].cta['schedule-call'].text : ''} ${
        !isTrail ? csColors[planType].cta['schedule-call'].bg : ''
    } ${
        !isTrail ? csColors[planType].cta['schedule-call'].border : ''
    } flex-grow border flex py-6 px-8 font-700 rounded-4 items-center justify-center cursor-pointer`;

    return (
        <div className="flex w-288 gap-24 flex-col">
            {hasSm && (
                <div className="flex flex-col w-full gap-16">
                    <Modal
                        title="Request Callback"
                        visible={isNoteModalVisible}
                        onOk={requestCallback}
                        onCancel={() => {
                            setNoteModalVisible(false);
                            setCallNotes('');
                        }}
                        destroyOnClose
                    >
                        <TextArea autoFocus onChange={onNoteChange} value={callNotes} />
                        <span className="text-11 mt-8 text-neutral-5">
                            e.g: I am available between 3-5 PM. Please call me at this time.
                        </span>
                    </Modal>
                    <div
                        className={`flex w-288 gap-8 items-center ${
                            !isTrail ? csColors[planType].innerText : 'text-white'
                        }`}
                    >
                        {planIcon}
                        <p className="font-700 text-14">{heading}</p>
                    </div>
                    <div
                        className={`flex flex-col rounded-4 gap-12 ${!isTrail ? 'p-12' : 'py-12'} ${
                            !isTrail ? csColors[planType].innerBg : ''
                        } ${!isTrail ? csColors[planType].innerText : 'text-white'}`}
                    >
                        {!isTrail && (
                            <div className="font-700 flex gap-8 items-center">
                                <EkaLogoCSCard />

                                <p className="capitalize text-16">
                                    {doc?.profile.professional.cs?.success_manager ?? ''}
                                </p>
                            </div>
                        )}
                        <p className="font-400">{subHeading}</p>
                    </div>
                    <div className="flex gap-16 items-center justify-between">
                        {isTrail ? (
                            <div
                                className={`flex-grow bg-white text-darwin-neutral-1000 border flex py-6 px-8 font-700 rounded-4 items-center justify-center cursor-pointer`}
                                onClick={goToSubscribe}
                            >
                                Activate Pro Now
                            </div>
                        ) : (
                            <Fragment>
                                {
                                    <div
                                        className={secondaryStyle}
                                        onClick={() => {
                                            if (canScheduleCall) {
                                                track('SCHEDULE_CALL', {
                                                    on: 'CUSTOMER_SUCCESS_CARD',
                                                    successManagerId:
                                                        doc?.profile.professional?.cs?.oid,
                                                    plan: planType,
                                                });

                                                const calendlyLink =
                                                    calendlyLinks[
                                                        doc?.profile.professional.cs!
                                                            .oid as keyof typeof calendlyLinks
                                                    ];

                                                window.open(calendlyLink, '_blank');
                                            } else {
                                                setNoteModalVisible(true);
                                            }
                                        }}
                                    >
                                        {canScheduleCall ? 'Schedule a call' : 'Request callback'}
                                    </div>
                                }
                                <div
                                    className={primaryStyle}
                                    onClick={() => {
                                        try {
                                            openCrispChat(
                                                'CHAT_NOW',
                                                {
                                                    on: 'CUSTOMER_SUCCESS_CARD',
                                                    plan: planType,
                                                    successManagerId:
                                                        doc?.profile.professional?.cs?.oid,
                                                },
                                                'Hello Success Manager',
                                            );
                                        } catch (error) {
                                            track('ERROR', {
                                                on: 'Customer Success Card',
                                                error: (error as Error).message,
                                            });
                                            console.error(error);
                                            captureError(error as Error);
                                        }
                                    }}
                                >
                                    Chat now
                                </div>
                            </Fragment>
                        )}
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-8">
                <img src="/images/sm-banner.png" />
                <p
                    className={`text-16 font-700 ${
                        plan === 'PLUS' ? 'text-darwin-neutral_new-1000' : 'text-white'
                    }`}
                >
                    Schedule your training with product experts
                </p>
                <div className="flex flex-col w-full gap-16">
                    <p
                        className={`text-14 font-400 ${
                            plan === 'PLUS' ? 'text-darwin-neutral_new-1000' : 'text-white'
                        }`}
                    >
                        Choose from 8 topics, your preferred language and time slot to schedule your
                        training from Eka Experts.
                    </p>
                    <div
                        onClick={gotoRequestTraining}
                        className="flex py-8 justify-center cursor-pointer items-center rounded-4 bg-darwin-accent-bright-blue-800 font-700 text-white w-full"
                    >
                        Schedule Training
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CSCard;

function pushRequestCallbackToSlack(obj: { [k: string]: string }): void {
    sendToSlack({
        key: 'tf',
        payload: [
            {
                blocks: [
                    ...Object.keys(obj).map((k) => ({
                        type: 'section',
                        text: {
                            type: 'mrkdwn',
                            text: `${k}: ${obj[k]}`,
                        },
                    })),
                ],
            },
        ],
    });
}
